"use strict";
/**
 * Brand Workspace v2: the structured brand fields, defined once (E19).
 *
 * The single source of truth for field keys, labels, hints, kinds, limits, source tags, the wizard's
 * steps and the value-philosophy options. The API (DTOs and the one writer), the extraction validator,
 * the migration and the web all read these, so a label or a limit cannot differ between them.
 *
 * Keys are FLAT (`coreValues`, not `companyVoice.coreValues`): they equal the `BrandProfile` columns,
 * the extraction output and the DTO keys. Social hashtags and Catchphrases keep their storage in
 * `Tenant.brandAssets.hashtags` / `.phrases` (spec:257); `BrandFieldDefinition.storage` says so.
 *
 * Enum values are UPPER_SNAKE and equal the Prisma enums (`apps/api/prisma/schema.prisma`), written as
 * `as const` arrays plus unions, the house style (ai-models.ts, design-composition.ts).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.VALUE_PHILOSOPHY_OPTIONS = exports.BRAND_WIZARD_STEP_COUNT = exports.BRAND_WIZARD_STEPS = exports.BRAND_SECTIONS = exports.WORKSPACE_DETAIL_FIELDS = exports.AUDIENCE_FIELD_DEFINITIONS = exports.BRAND_FIELD_DEFINITIONS = exports.AI_SUGGESTED_TAG_LABEL = exports.BRAND_SOURCE_TAG_LABELS = exports.BRAND_SOURCE_TAGS = exports.BRAND_AUDIENCES_FULL_TEXT = exports.BRAND_LIMITS = exports.BRAND_MIGRATION_STATUSES = exports.BRAND_ONBOARDING_START_MODES = exports.BRAND_ONBOARDING_STATUSES = exports.VALUE_PHILOSOPHIES = exports.EDIT_PAGE_LIST_KEYS = exports.AUDIENCE_FIELD_KEYS = exports.BRAND_FIELD_KEYS = exports.BRAND_LIST_FIELD_KEYS = exports.BRAND_TEXT_FIELD_KEYS = void 0;
exports.isBrandProfileLive = isBrandProfileLive;
exports.canonicalSources = canonicalSources;
exports.emptyBrandFieldValues = emptyBrandFieldValues;
exports.emptyBrandAudienceValues = emptyBrandAudienceValues;
exports.priceTierLabel = priceTierLabel;
exports.valuePhilosophyFromLabel = valuePhilosophyFromLabel;
exports.normalizeBrandText = normalizeBrandText;
exports.normalizeBrandListItem = normalizeBrandListItem;
exports.brandListItemKey = brandListItemKey;
exports.normalizeBrandList = normalizeBrandList;
exports.checkBrandFieldValue = checkBrandFieldValue;
exports.checkAudienceFieldValue = checkAudienceFieldValue;
exports.isBrandValueEmpty = isBrandValueEmpty;
// ── Keys ──────────────────────────────────────────────────────────────────────
/** Single-value text fields (Text or Textarea). */
exports.BRAND_TEXT_FIELD_KEYS = [
    'coreValues',
    'mission',
    'vision',
    'problems',
    'solutions',
    'coreMessage',
    'personality',
    'tone',
    'language',
    'approach',
    'promise',
    'geographicFootprint',
    'differentiators',
];
/** List fields: the five Vocabulary tag clouds and the Writing guidelines table. */
exports.BRAND_LIST_FIELD_KEYS = [
    'keywords',
    'alwaysSay',
    'neverSay',
    'socialHashtags',
    'catchphrases',
    'writingGuidelines',
];
/** Edit-page and wizard order (spec:211-240). */
exports.BRAND_FIELD_KEYS = [
    'coreValues',
    'mission',
    'vision',
    'problems',
    'solutions',
    'coreMessage',
    'personality',
    'tone',
    'language',
    'approach',
    'promise',
    'keywords',
    'alwaysSay',
    'neverSay',
    'socialHashtags',
    'catchphrases',
    'writingGuidelines',
    'geographicFootprint',
    'differentiators',
    'valuePhilosophy',
];
exports.AUDIENCE_FIELD_KEYS = [
    'identity',
    'characteristics',
    'emotionalMotivation',
    'desiredVoice',
    'messagingPriorities',
    'keyMessage',
];
/** Editable on the Edit page (spec:176; C41: all five Vocabulary lists and Writing guidelines). */
exports.EDIT_PAGE_LIST_KEYS = [
    'keywords',
    'alwaysSay',
    'neverSay',
    'socialHashtags',
    'catchphrases',
    'writingGuidelines',
];
// ── Enums (values equal the Prisma enums) ─────────────────────────────────────
exports.VALUE_PHILOSOPHIES = ['BUDGET_FRIENDLY', 'AFFORDABLE', 'PREMIUM', 'LUXURY'];
exports.BRAND_ONBOARDING_STATUSES = ['NOT_STARTED', 'IN_PROGRESS', 'FINISHED'];
exports.BRAND_ONBOARDING_START_MODES = ['UPLOAD', 'MANUAL'];
/** MIGRATED and SKIPPED_EMPTY are live (agents read the structured fields); PENDING is not. */
exports.BRAND_MIGRATION_STATUSES = ['PENDING', 'MIGRATED', 'SKIPPED_EMPTY'];
/** Agents read the structured fields of a workspace in this state (the rest use the legacy text). */
function isBrandProfileLive(s) {
    return s === 'MIGRATED' || s === 'SKIPPED_EMPTY';
}
// ── Limits (spec:269; E19 keeps the 120-character name) ───────────────────────
exports.BRAND_LIMITS = {
    workspaceName: 120,
    websiteUrl: 2048,
    /** Text fields: Core brand message, Audience identity, Key message. */
    text: 200,
    /** Every Textarea. */
    textarea: 2000,
    /** Characters per tag. */
    tag: 60,
    tagsPerList: 50,
    /** Characters per writing guideline. */
    guideline: 300,
    guidelines: 50,
    /** A product rule, not a proposal (spec:269). */
    audiences: 10,
    // R4 scanner:
    urls: 10,
    pageTextChars: 20_000,
    pageBytes: 2 * 1024 * 1024,
    pageTimeoutMs: 10_000,
    redirects: 3,
    /** Today's upload cap (tenants.controller.ts). */
    uploadBytes: 10 * 1024 * 1024,
    /** Characters of document (or legacy) text the extraction reads. */
    documentTextChars: 40_000,
    /** Request-body sanity caps for Social hashtags / Catchphrases, whose stored items are kept as they are (E4). */
    legacyListHardMax: 500,
    legacyItemHardMax: 1000,
};
exports.BRAND_AUDIENCES_FULL_TEXT = 'Maximum of 10 audiences. Remove one to add another.';
// ── Source tags (spec:103-111) ────────────────────────────────────────────────
/** Canonical order: the order tags are stored and shown in. */
exports.BRAND_SOURCE_TAGS = ['brandDocument', 'brandWebsite', 'manualEntry'];
exports.BRAND_SOURCE_TAG_LABELS = {
    brandDocument: 'Brand Document',
    brandWebsite: 'Brand website',
    manualEntry: 'Manual Entry',
};
exports.AI_SUGGESTED_TAG_LABEL = 'AI suggested';
/** Dedupe, drop unknown values, canonical order. */
function canonicalSources(tags) {
    if (!Array.isArray(tags))
        return [];
    return exports.BRAND_SOURCE_TAGS.filter((tag) => tags.includes(tag));
}
function emptyBrandFieldValues() {
    return {
        coreValues: null,
        mission: null,
        vision: null,
        problems: null,
        solutions: null,
        coreMessage: null,
        personality: null,
        tone: null,
        language: null,
        approach: null,
        promise: null,
        keywords: [],
        alwaysSay: [],
        neverSay: [],
        socialHashtags: [],
        catchphrases: [],
        writingGuidelines: [],
        geographicFootprint: null,
        differentiators: null,
        valuePhilosophy: null,
    };
}
function emptyBrandAudienceValues() {
    return {
        identity: null,
        characteristics: null,
        emotionalMotivation: null,
        desiredVoice: null,
        messagingPriorities: null,
        keyMessage: null,
    };
}
const textarea = (key, section, label, hint, wizardStep) => ({
    key,
    section,
    label,
    hint,
    kind: 'textarea',
    maxLength: exports.BRAND_LIMITS.textarea,
    wizardStep,
    editableOnEditPage: false,
    storage: 'profile',
});
const tags = (key, label, hint, storage = 'profile') => ({
    key,
    section: 'vocabulary',
    label,
    hint,
    kind: 'tags',
    maxLength: exports.BRAND_LIMITS.tag,
    maxItems: exports.BRAND_LIMITS.tagsPerList,
    wizardStep: 5,
    editableOnEditPage: true,
    storage,
});
/** Labels and hints verbatim from spec:211-240. In Edit-page order. */
exports.BRAND_FIELD_DEFINITIONS = {
    coreValues: textarea('coreValues', 'companyVoice', 'Core values', 'What does your company value and stand for?', 2),
    mission: textarea('mission', 'companyVoice', 'Mission', 'What is your company trying to solve?', 2),
    vision: textarea('vision', 'companyVoice', 'Vision', "What is your company's ultimate goal?", 2),
    problems: textarea('problems', 'brandIdentity', 'Problems your brand addresses', "What are the biggest problems for your brand's customers?", 3),
    solutions: textarea('solutions', 'brandIdentity', 'Solutions your brand offers', "How does your brand solve its customers' problems?", 3),
    coreMessage: {
        key: 'coreMessage',
        section: 'brandVoice',
        label: 'Core brand message',
        hint: null,
        kind: 'text',
        maxLength: exports.BRAND_LIMITS.text,
        wizardStep: 4,
        editableOnEditPage: false,
        storage: 'profile',
    },
    personality: textarea('personality', 'brandVoice', 'Personality', 'What is your brand like?', 4),
    tone: textarea('tone', 'brandVoice', 'Tone', 'How does the way your brand speaks make people feel about your brand?', 4),
    language: textarea('language', 'brandVoice', 'Language', 'How does your brand communicate?', 4),
    approach: textarea('approach', 'brandVoice', 'Approach', 'How does your brand work with its customers?', 4),
    promise: textarea('promise', 'brandVoice', 'Brand promise', 'What does your brand guarantee to its customers?', 4),
    keywords: tags('keywords', 'Keywords', 'Words and themes your brand is known for.'),
    alwaysSay: tags('alwaysSay', 'Always say', 'Words and phrases to use.'),
    neverSay: tags('neverSay', 'Never say', 'Words and phrases to avoid.'),
    socialHashtags: tags('socialHashtags', 'Social hashtags', 'Hashtags your brand uses on social posts.', 'brandAssets.hashtags'),
    catchphrases: tags('catchphrases', 'Catchphrases and slogans', 'Phrases and slogans your brand uses.', 'brandAssets.phrases'),
    writingGuidelines: {
        key: 'writingGuidelines',
        section: 'writingGuidelines',
        label: 'Guideline',
        hint: 'Rules the AI follows in everything it writes for your brand.',
        kind: 'guidelines',
        maxLength: exports.BRAND_LIMITS.guideline,
        maxItems: exports.BRAND_LIMITS.guidelines,
        wizardStep: 6,
        editableOnEditPage: true,
        storage: 'profile',
        placeholder: "Don't promise specific health outcomes.",
    },
    geographicFootprint: textarea('geographicFootprint', 'market', 'Geographic footprint', "Where are your brand's products and services available?", 8),
    differentiators: textarea('differentiators', 'market', 'Differentiators', 'What sets your brand apart from its competitors?', 8),
    valuePhilosophy: {
        key: 'valuePhilosophy',
        section: 'market',
        label: 'Value philosophy',
        hint: 'How would your brand describe its value?',
        kind: 'valuePhilosophy',
        maxLength: 0,
        wizardStep: 8,
        editableOnEditPage: false,
        storage: 'profile',
    },
};
/** Labels and hints verbatim from spec:231-236. */
exports.AUDIENCE_FIELD_DEFINITIONS = {
    identity: {
        key: 'identity',
        label: 'Audience identity',
        hint: 'Who is the audience?',
        kind: 'text',
        maxLength: exports.BRAND_LIMITS.text,
    },
    characteristics: {
        key: 'characteristics',
        label: 'Audience characteristics',
        hint: 'How would your brand describe their motivations and personality?',
        kind: 'textarea',
        maxLength: exports.BRAND_LIMITS.textarea,
    },
    emotionalMotivation: {
        key: 'emotionalMotivation',
        label: 'Emotional motivation',
        hint: "What feelings would motivate them to purchase your brand's products or services?",
        kind: 'textarea',
        maxLength: exports.BRAND_LIMITS.textarea,
    },
    desiredVoice: {
        key: 'desiredVoice',
        label: 'Desired voice',
        hint: 'How does your brand speak to them?',
        kind: 'textarea',
        maxLength: exports.BRAND_LIMITS.textarea,
    },
    messagingPriorities: {
        key: 'messagingPriorities',
        label: 'Messaging priorities',
        hint: 'What does your brand talk about with them?',
        kind: 'textarea',
        maxLength: exports.BRAND_LIMITS.textarea,
    },
    keyMessage: {
        key: 'keyMessage',
        label: 'Key message',
        hint: 'The one thing your brand wants to say the most to them.',
        kind: 'text',
        maxLength: exports.BRAND_LIMITS.text,
    },
};
/** The Create page and the Edit page's workspace details (spec:42-43, :213-214). Both are required. */
exports.WORKSPACE_DETAIL_FIELDS = {
    name: {
        key: 'name',
        label: 'Brand Workspace Name',
        hint: null,
        required: true,
        maxLength: exports.BRAND_LIMITS.workspaceName,
        placeholder: 'e.g. Northwind Orthopedics',
    },
    websiteUrl: {
        key: 'websiteUrl',
        label: 'Public Website',
        hint: null,
        required: true,
        maxLength: exports.BRAND_LIMITS.websiteUrl,
    },
};
exports.BRAND_SECTIONS = [
    { key: 'workspaceDetails', title: 'Workspace details' },
    { key: 'companyVoice', title: 'Company voice and identity' },
    { key: 'brandIdentity', title: 'Brand identity' },
    { key: 'brandVoice', title: 'Brand voice' },
    { key: 'vocabulary', title: 'Vocabulary' },
    { key: 'writingGuidelines', title: 'Writing guidelines' },
    { key: 'audiences', title: 'Audiences' },
    { key: 'market', title: 'Market' },
];
exports.BRAND_WIZARD_STEPS = [
    { step: 1, pill: 'Start', section: null, fields: [] },
    { step: 2, pill: 'Company', section: 'companyVoice', fields: ['coreValues', 'mission', 'vision'] },
    { step: 3, pill: 'Identity', section: 'brandIdentity', fields: ['problems', 'solutions'] },
    {
        step: 4,
        pill: 'Voice',
        section: 'brandVoice',
        fields: ['coreMessage', 'personality', 'tone', 'language', 'approach', 'promise'],
    },
    {
        step: 5,
        pill: 'Vocabulary',
        section: 'vocabulary',
        fields: ['keywords', 'alwaysSay', 'neverSay', 'socialHashtags', 'catchphrases'],
    },
    { step: 6, pill: 'Guidelines', section: 'writingGuidelines', fields: ['writingGuidelines'] },
    { step: 7, pill: 'Audiences', section: 'audiences', fields: [] },
    {
        step: 8,
        pill: 'Market',
        section: 'market',
        fields: ['geographicFootprint', 'differentiators', 'valuePhilosophy'],
    },
];
exports.BRAND_WIZARD_STEP_COUNT = 8;
/** spec:244-249, verbatim. */
exports.VALUE_PHILOSOPHY_OPTIONS = [
    {
        value: 'BUDGET_FRIENDLY',
        label: 'Budget-Friendly',
        tier: 1,
        description: 'Accessible pricing for price-conscious customers',
    },
    { value: 'AFFORDABLE', label: 'Affordable', tier: 2, description: 'Competitive pricing for everyday customers' },
    {
        value: 'PREMIUM',
        label: 'Premium',
        tier: 3,
        description: 'Higher prices reflecting quality, features, or brand reputation',
    },
    { value: 'LUXURY', label: 'Luxury', tier: 4, description: 'High-end pricing emphasizing exclusivity and prestige' },
];
/** The screen-reader text for an option's dollar signs: "Price tier 2 of 4" (spec:251). */
function priceTierLabel(tier) {
    return `Price tier ${tier} of ${exports.VALUE_PHILOSOPHY_OPTIONS.length}`;
}
/** Spaces, hyphens and underscores dropped, lower case: "Budget-Friendly", "budget friendly" and
 *  "BUDGET_FRIENDLY" all read the same. */
const optionKey = (text) => text.toLowerCase().replace(/[\s_-]+/g, '');
/**
 * The value an option's label names, compared in lower case without spaces or hyphens; null when it
 * names none. The extraction validator reads the model's answer (a label) with it.
 */
function valuePhilosophyFromLabel(label) {
    if (typeof label !== 'string')
        return null;
    const key = optionKey(label.trim());
    if (key === '')
        return null;
    const option = exports.VALUE_PHILOSOPHY_OPTIONS.find((o) => optionKey(o.label) === key || optionKey(o.value) === key);
    return option ? option.value : null;
}
// ── Normalizers and checks (pure; the writer, the extraction validator and the web use them) ──
/** C0 controls other than tab and newline. Postgres TEXT refuses NUL. */
const TEXT_CONTROLS = /[\u0000-\u0008\u000B-\u001F]/g;
/** C0 controls that are not whitespace (tab, newline, vertical tab, form feed and CR become spaces). */
const ITEM_CONTROLS = /[\u0000-\u0008\u000E-\u001F]/g;
/** Strip NUL and C0 controls except \n and \t, turn \r\n and \r into \n, trim. '' → null. */
function normalizeBrandText(raw) {
    if (typeof raw !== 'string')
        return null;
    const text = raw.replace(/\r\n?/g, '\n').replace(TEXT_CONTROLS, '').trim();
    return text === '' ? null : text;
}
/**
 * One-line item: controls removed, whitespace runs → one space, trimmed. Social hashtags: leading '#'s
 * stripped, then one '#'. '' when nothing is left.
 */
function normalizeBrandListItem(key, raw) {
    if (typeof raw !== 'string')
        return '';
    const line = raw.replace(ITEM_CONTROLS, '').replace(/\s+/g, ' ').trim();
    if (key !== 'socialHashtags')
        return line;
    const tag = line.replace(/^#+/, '').trim();
    return tag === '' ? '' : `#${tag}`;
}
/** The case-insensitive identity of a list item: NFKC, lower case, whitespace runs collapsed. */
function brandListItemKey(item) {
    return item.normalize('NFKC').toLowerCase().replace(/\s+/g, ' ').trim();
}
/** Normalize each item, drop empties, drop case-insensitive duplicates (the first wins). */
function normalizeBrandList(key, raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    const seen = new Set();
    for (const item of raw) {
        const value = normalizeBrandListItem(key, item);
        if (value === '')
            continue;
        const identity = brandListItemKey(value);
        if (seen.has(identity))
            continue;
        seen.add(identity);
        out.push(value);
    }
    return out;
}
/** 2000 → "2,000", the same in every runtime (no toLocaleString). */
function formatCount(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
/** How messages name a list. The Writing guidelines table's label is its column header, "Guideline". */
function listName(key) {
    return key === 'writingGuidelines' ? 'Writing guidelines' : exports.BRAND_FIELD_DEFINITIONS[key].label;
}
function checkText(field, label, maxLength, value) {
    if (value === null || value === undefined)
        return [];
    if (typeof value !== 'string')
        return [{ field, code: 'WRONG_TYPE', message: `${label} must be text.` }];
    if (value.length > maxLength) {
        return [{ field, code: 'TOO_LONG', message: `${label} can be up to ${formatCount(maxLength)} characters.` }];
    }
    return [];
}
/**
 * Limit check on an already-normalized value. Never clips: callers decide (the API refuses, the
 * extraction drops or cuts). Text: a string or null. Lists: an array of strings. Value philosophy:
 * one of the four values, or null.
 */
function checkBrandFieldValue(key, value) {
    const def = exports.BRAND_FIELD_DEFINITIONS[key];
    if (!def)
        return [{ field: String(key), code: 'WRONG_TYPE', message: `${String(key)} is not a brand field.` }];
    switch (def.kind) {
        case 'text':
        case 'textarea':
            return checkText(key, def.label, def.maxLength, value);
        case 'valuePhilosophy': {
            if (value === null || value === undefined)
                return [];
            const message = `Choose one of the four options for ${def.label}.`;
            if (typeof value !== 'string')
                return [{ field: key, code: 'WRONG_TYPE', message }];
            return exports.VALUE_PHILOSOPHIES.includes(value)
                ? []
                : [{ field: key, code: 'NOT_AN_OPTION', message }];
        }
        case 'tags':
        case 'guidelines': {
            const name = listName(key);
            if (!Array.isArray(value) || value.some((item) => typeof item !== 'string')) {
                return [{ field: key, code: 'WRONG_TYPE', message: `${name} must be a list of entries.` }];
            }
            const issues = [];
            const maxItems = def.maxItems ?? 0;
            if (value.length > maxItems) {
                issues.push({
                    field: key,
                    code: 'TOO_MANY',
                    message: `${name} can have up to ${formatCount(maxItems)} entries. Remove one to add another.`,
                });
            }
            if (value.some((item) => item.length > def.maxLength)) {
                issues.push({
                    field: key,
                    code: 'ITEM_TOO_LONG',
                    message: `Each entry in ${name} can be up to ${formatCount(def.maxLength)} characters.`,
                });
            }
            return issues;
        }
    }
}
function checkAudienceFieldValue(key, value) {
    const def = exports.AUDIENCE_FIELD_DEFINITIONS[key];
    if (!def)
        return [{ field: String(key), code: 'WRONG_TYPE', message: `${String(key)} is not an audience field.` }];
    return checkText(key, def.label, def.maxLength, value);
}
/** null, undefined, '' (after trim) or []. */
function isBrandValueEmpty(value) {
    if (value === null || value === undefined)
        return true;
    if (typeof value === 'string')
        return value.trim() === '';
    if (Array.isArray(value))
        return value.length === 0;
    return false;
}
//# sourceMappingURL=brand-fields.js.map